import { formatWholeMoney } from './valueFormat';
/** Formatting helpers for KPI values, deltas and statuses. */

export const STATUS_LABEL = { good: 'On target', warn: 'Near target', bad: 'Off target', none: 'No target' };
export const CADENCE_LABEL = { day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly' };
export const PERIOD_LABEL = { day: 'today', week: 'this week', month: 'this month', quarter: 'this quarter', year: 'this year' };
export const PREVIOUS_LABEL = { day: 'yesterday', week: 'last week', month: 'last month', quarter: 'last quarter', year: 'last year' };

export function formatKpiValue(value, format = 'number', unit = '') {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  switch (format) {
    case 'percent': {
      const pct = n * 100;
      // One decimal when it carries information (85.7%), none otherwise (90%).
      const rounded = Math.round(pct * 10) / 10;
      return `${rounded.toLocaleString(undefined, { maximumFractionDigits: Number.isInteger(rounded) ? 0 : 1 })}%`;
    }
    case 'currency':
      return formatWholeMoney(n);
    case 'duration':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit || 'h'}`;
    default: {
      const s = n.toLocaleString(undefined, { maximumFractionDigits: Number.isInteger(n) ? 0 : 2 });
      return unit ? `${s} ${unit}` : s;
    }
  }
}

/** "↑ 3.2 pts vs last week" style delta description. Returns null when not computable. */
export function describeChange(result) {
  if (!result || result.change === null || result.change === undefined) return null;
  const { change, changePct, kpi } = result;
  const up = change > 0;
  const arrow = change === 0 ? '→' : up ? '↑' : '↓';
  // For percent KPIs show the change in points; otherwise the relative change.
  let text;
  if (kpi.format === 'percent') {
    text = `${Math.abs(change * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })} pts`;
  } else if (changePct !== null && changePct !== undefined) {
    text = `${Math.abs(changePct * 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}%`;
  } else {
    text = formatKpiValue(Math.abs(change), kpi.format, kpi.unit);
  }
  // Direction-aware colouring: up is good only when higher is better.
  const improving = change === 0 ? null : (up === (kpi.direction !== 'lower'));
  return { arrow, text, improving, label: `${arrow} ${text} vs ${PREVIOUS_LABEL[kpi.cadence] || 'previous'}` };
}

/** Slug for a KPI key from its name. */
export function slugify(name) {
  return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 'k_$1');
}

/** The query behind a KPI's figure for its period: what its rows are drilled from. */
export function kpiDrillQuery(result) {
  const { kpi, period } = result || {};
  if (!kpi?.measure) return null;
  const range = period?.dateRange ?? period?.range;
  return {
    measures: [kpi.measure],
    ...(kpi.timeDimension && range ? { timeDimensions: [{ dimension: kpi.timeDimension, dateRange: range }] } : {}),
    filters: kpi.filters || [],
  };
}
